import { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { Loader2, Sparkles, Trash2, Eye, EyeOff, ExternalLink, RefreshCw, Plus, History } from "lucide-react";

const TOKEN_KEY = "econation_admin_token";
const getToken = () => localStorage.getItem(TOKEN_KEY);

const CATEGORIES = [
  { slug: "cee-technique", label: "CEE · Technique", color: "#10B981" },
  { slug: "renovation", label: "Rénovation", color: "#0055A4" },
  { slug: "environnement", label: "Environnement", color: "#0EA5E9" },
  { slug: "reglementation", label: "Réglementation", color: "#8B5CF6" },
];

async function authFetch(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}: ${t}`);
  }
  return res.json();
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function BlogAdmin() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [generating, setGenerating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [seedStatus, setSeedStatus] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await authFetch("/blog/admin/articles");
      setArticles(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Poll seed status while running
  useEffect(() => {
    if (!seeding) return undefined;
    const iv = setInterval(async () => {
      try {
        const st = await authFetch("/blog/admin/seed/status");
        setSeedStatus(st);
        if (!st.running) {
          setSeeding(false);
          await load();
        }
      } catch { /* noop */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [seeding]);

  const startSeed = async () => {
    if (!window.confirm("Générer 10 articles historiques antidatés (fév 2023 → nov 2025) ? Cela prend environ 4 minutes en arrière-plan.")) return;
    setErr("");
    try {
      const res = await authFetch("/blog/admin/seed", { method: "POST" });
      setSeedStatus(res);
      setSeeding(true);
    } catch (e) {
      setErr(`Seed impossible : ${e.message}`);
    }
  };

  const generate = async () => {
    setGenerating(true);
    setErr("");
    try {
      const qs = selectedCategory ? `?category=${selectedCategory}` : "";
      await authFetch(`/blog/admin/generate${qs}`, { method: "POST" });
      await load();
    } catch (e) {
      setErr(`Génération échouée : ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const togglePublish = async (a) => {
    try {
      await authFetch(`/blog/admin/articles/${a.id}`, {
        method: "PATCH",
        body: JSON.stringify({ published: !a.published }),
      });
      await load();
    } catch (e) {
      setErr(e.message);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer définitivement "${a.title}" ?`)) return;
    try {
      await authFetch(`/blog/admin/articles/${a.id}`, { method: "DELETE" });
      setArticles((prev) => prev.filter((x) => x.id !== a.id));
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div data-testid="blog-admin" className="space-y-6">
      {/* Actions bar */}
      <div className="bg-white rounded-2xl border border-black/[0.06] p-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#10B981]" />
          <span className="font-semibold text-[#0F172A]">Générer un nouvel article</span>
        </div>
        <select
          data-testid="blog-category-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="h-11 rounded-xl border border-black/[0.08] bg-white text-sm px-3 focus:outline-none focus:border-[#10B981]"
        >
          <option value="">Catégorie aléatoire (pondérée)</option>
          {CATEGORIES.map((c) => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </select>
        <button
          data-testid="blog-generate-btn"
          onClick={generate}
          disabled={generating}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#10B981] hover:bg-[#059669] disabled:opacity-60 text-white text-sm font-semibold transition-colors"
        >
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {generating ? "Génération IA…" : "Générer avec Claude"}
        </button>
        <button
          data-testid="blog-refresh-btn"
          onClick={load}
          className="ml-auto inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-black/[0.08] hover:border-[#10B981]/50 text-[#475569] hover:text-[#0F172A] text-sm font-medium transition-colors"
        >
          <RefreshCw className="h-4 w-4" /> Actualiser
        </button>
      </div>

      {/* Seed banner — visible only if less than 10 articles OR seeding in progress */}
      {(articles.length < 10 || seeding) && (
        <div data-testid="blog-seed-banner" className="bg-gradient-to-r from-[#064E3B] to-[#10B981] rounded-2xl p-6 text-white flex flex-wrap items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-white/15 grid place-items-center flex-shrink-0">
            <History className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="font-display text-lg font-bold">Peupler le blog avec 10 articles historiques</div>
            <div className="text-sm text-white/80 mt-0.5">
              {seeding && seedStatus
                ? `Génération en cours… ${seedStatus.generated || 0}/${seedStatus.target || 10} articles créés`
                : "Génère 10 articles antidatés de février 2023 à novembre 2025 (via Claude Sonnet 4.6). ~4 minutes en arrière-plan."}
            </div>
          </div>
          <button
            data-testid="blog-seed-btn"
            onClick={startSeed}
            disabled={seeding}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white text-[#064E3B] hover:bg-white/90 disabled:opacity-60 text-sm font-semibold transition-colors"
          >
            {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
            {seeding ? "Génération…" : "Lancer le seed historique"}
          </button>
        </div>
      )}

      {err && (
        <div data-testid="blog-error" className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{err}</div>
      )}

      {/* Articles table */}
      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 text-[#10B981] animate-spin" /></div>
      ) : articles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/[0.06] p-16 text-center text-[#94A3B8]">
          Aucun article. Générez le premier !
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-[#F8FAFC] border-b border-black/[0.06] text-[10px] uppercase tracking-widest text-[#64748B] font-semibold">
            <div className="col-span-5">Article</div>
            <div className="col-span-2">Catégorie</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1">Statut</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <ul className="divide-y divide-black/[0.05]">
            {articles.map((a) => {
              const cat = CATEGORIES.find((c) => c.slug === a.category);
              return (
                <li key={a.id} data-testid={`blog-row-${a.id}`} className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                  <div className="col-span-5 min-w-0 flex items-center gap-3">
                    <img src={a.image_url} alt="" className="h-11 w-16 rounded-lg object-cover flex-shrink-0 bg-[#F1F5F9]" loading="lazy" />
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0F172A] text-sm truncate">{a.title}</div>
                      <div className="text-xs text-[#94A3B8] truncate">{a.reading_time} min · {a.tags?.slice(0, 2).join(" · ")}</div>
                    </div>
                  </div>
                  <div className="col-span-2 text-xs">
                    <span className="inline-block px-2 py-1 rounded-md font-medium" style={{ backgroundColor: `${cat?.color || "#10B981"}1A`, color: cat?.color || "#10B981" }}>
                      {cat?.label || a.category}
                    </span>
                  </div>
                  <div className="col-span-2 text-xs text-[#64748B]">{formatDate(a.created_at)}</div>
                  <div className="col-span-1">
                    <span className={`inline-block h-2 w-2 rounded-full ${a.published ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`} />
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <a
                      href={`/blog/${a.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`blog-view-${a.id}`}
                      className="h-9 w-9 grid place-items-center rounded-lg text-[#64748B] hover:bg-black/[0.04] hover:text-[#0F172A]"
                      title="Voir l'article"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    <button
                      data-testid={`blog-toggle-${a.id}`}
                      onClick={() => togglePublish(a)}
                      className="h-9 w-9 grid place-items-center rounded-lg text-[#64748B] hover:bg-black/[0.04] hover:text-[#0F172A]"
                      title={a.published ? "Dépublier" : "Publier"}
                    >
                      {a.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button
                      data-testid={`blog-delete-${a.id}`}
                      onClick={() => remove(a)}
                      className="h-9 w-9 grid place-items-center rounded-lg text-[#64748B] hover:bg-red-50 hover:text-red-600"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
